import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  savePhoneNumber,
  getPhoneNumberWithStatus,
  clearPhoneNumber,
  getVerificationCode,
  saveVerificationCode,
  getAddress,
  saveAddress,
  clearAddress as clearStoredAddress,
  getZoningData,
  saveZoningData,
  clearZoningData as clearStoredZoningData,
  getRecipientInfo,
  saveRecipientInfo,
  clearResendCooldown,
} from '../utils/localStorage';
import {
  getCurrentTimePlusTwoHours,
  getDateTimestamp,
  getTimeSlotTimestamp,
  parseDateInput,
  formatDateForDisplay,
} from '../utils/deliveryDateUtils';
import { isDevelopment, ANALYTICS_SOURCE } from '../env/config';
import { isMobile } from 'react-device-detect';
import { toast } from 'react-toastify';
import { getSmartSendOrderData } from '../services/getSmartSendOrderData.api';
import { formatPhoneToE164 } from '../services/sendVerificationCode.api';
import { hasValidPersonalMessage } from '../utils/personalMessage';
import analytics from '../../../library/segmentAnalytics';

/**
 * SmartSend Context - Manages shared state across SmartSend_v2 components
 *
 * State includes:
 * - Verification flow (phone, code verification, step tracking)
 * - Device detection (isDesktop for conditional flow behavior)
 * - Address information (address object with timezone)
 * - Delivery details (date, time, instructions)
 * - Order and authentication data
 * - App mode (inApp vs web)
 */

const SmartSendContext = createContext(undefined);

export const SmartSendProvider = ({
  children,
  orderId,
  tokenId, // Optional auth token from URL params (provided by consumer app, null for web G-links)
  initialCode, // URL query param code for deep linking (passed as 'code' to backend)
  inApp = false,
  orderData,
  secureVerifyHeader,
}) => {
  // ============================================
  // AUTHENTICATION & ORDER STATE
  // ============================================
  const [token, setToken] = useState(tokenId || null);
  const [order, setOrder] = useState(orderData || null);
  const [secureHeader, setSecureHeader] = useState(secureVerifyHeader || null);
  const [appMode] = useState(inApp);
  const [pendingRetryCallback, setPendingRetryCallback] = useState(null);
  // URL query param code (from deep linking) - sent as 'code' to backend
  const [urlCode] = useState(initialCode || null);

  // Use ref for callback to avoid re-registering event listener on every change
  const pendingRetryCallbackRef = useRef(null);
  // Track if nonce refresh is in progress to prevent race conditions
  const isRefreshingNonceRef = useRef(false);

  // ============================================
  // VERIFICATION FLOW STATE
  // ============================================
  const [verificationStep, setVerificationStep] = useState('phone'); // 'phone' | 'code' | 'personalMessage' | 'complete'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState(''); // SMS code for final submission
  const [isLoadingDeepLink, setIsLoadingDeepLink] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);

  // ============================================
  // DEVICE DETECTION STATE
  // ============================================
  // Desktop users auto-advance to delivery form after verification
  // Mobile users see "Redeem in App" / "Skip" buttons
  const [isDesktop] = useState(() => {
    // Check if running in browser environment
    if (typeof window === 'undefined') return false;
    // Use react-device-detect to determine device type
    // Treat tablets as mobile for safety (better UX to offer app option)
    return !isMobile;
  });

  // ============================================
  // ADDRESS STATE (with timezone support)
  // ============================================
  const [address, setAddress] = useState(null);

  // ============================================
  // ZONING DATA STATE
  // ============================================
  const [zoningData, setZoningData] = useState(null);
  const [isAddressValidated, setIsAddressValidated] = useState(false);

  // ============================================
  // DELIVERY DETAILS STATE
  // ============================================
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  // ============================================
  // RECIPIENT INFO STATE (for RECIPIENT_INFO_REQUIRED handling)
  // ============================================
  const [recipientInfo, setRecipientInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // ============================================
  // INITIALIZATION EFFECT
  // ============================================
  useEffect(() => {
    // Update order data when it changes from parent
    if (orderData) {
      setOrder(orderData);
    }

    // Update secure header when it changes from parent
    if (secureVerifyHeader) {
      setSecureHeader(secureVerifyHeader);
    }

    // Initialize verification flow based on app mode
    if (inApp) {
      // If opened in app, check if personal message exists before skipping to delivery form
      // URL code is passed via initialCode prop (stored as urlCode state)
      // Use order state (which gets updated from orderData) to ensure we have the latest data
      // If orderData is not available yet, wait for it to be loaded
      const orderToCheck = orderData || order;
      if (orderToCheck) {
        const hasPersonalMessage = hasValidPersonalMessage(orderToCheck);
        const debugInfo = {
          hasOrderData: !!orderData,
          hasOrder: !!order,
          hasPersonalMessage,
          pmId: orderToCheck?.pmId,
          pmIdType: typeof orderToCheck?.pmId,
          pmIdValue: orderToCheck?.pmId,
          verificationStep: hasPersonalMessage ? 'personalMessage' : 'complete',
          inApp,
        };
        if (isDevelopment()) {
          console.log('[SmartSend Context] In-app initialization:', debugInfo);
        }
        // Only update step if we have order data - this ensures we check for personal message
        setVerificationStep(hasPersonalMessage ? 'personalMessage' : 'complete');
      } else {
        // If order data hasn't loaded yet, keep current step (don't change it)
        // This prevents skipping personal message when orderData loads asynchronously
        // The step will be set correctly once orderData becomes available
        // Don't set to 'complete' prematurely - wait for order data
        if (isDevelopment()) {
          console.log('[SmartSend Context] In-app mode: waiting for order data to load', {
            hasOrderData: !!orderData,
            hasOrder: !!order,
            currentStep: verificationStep,
          });
        }
      }
    } else if (orderId) {
      // ========================================
      // SECURITY MODEL: Phone Verification Persistence
      // ========================================
      const phoneData = getPhoneNumberWithStatus(orderId);
      const savedCode = getVerificationCode(orderId);

      if (phoneData && phoneData.isPhoneVerified && savedCode) {
        // Fully verified - check if personal message exists
        setPhoneNumber(phoneData.phoneNumber);
        setVerificationCode(savedCode);
        setIsCodeVerified(true);

        // Check if personal message exists - show it before address selection
        // Use order state (which gets updated from orderData) to ensure we have the latest data
        const orderToCheck = orderData || order;
        if (orderToCheck) {
          const hasPersonalMessage = hasValidPersonalMessage(orderToCheck);
          setVerificationStep(hasPersonalMessage ? 'personalMessage' : 'complete');
        }
      } else if (phoneData && !phoneData.isPhoneVerified) {
        // Phone number saved but not verified - show code entry screen
        setPhoneNumber(phoneData.phoneNumber);
        setVerificationStep('code');
      }
    }

    if (orderId) {
      // Initialize address from localStorage
      const savedAddress = getAddress(orderId);
      if (savedAddress) {
        setAddress(savedAddress);
      }

      // Initialize zoning data from localStorage
      const savedZoningData = getZoningData(orderId);
      if (savedZoningData) {
        setZoningData(savedZoningData);
        setIsAddressValidated(true); // Auto-validate if address exists in localStorage
      }

      // Initialize recipient info from localStorage
      const savedRecipientInfo = getRecipientInfo(orderId);
      if (savedRecipientInfo) {
        setRecipientInfo(savedRecipientInfo);
      }
    }
  }, [inApp, orderData, orderId, secureVerifyHeader, order]);

  // Sync callback to ref whenever it changes
  useEffect(() => {
    pendingRetryCallbackRef.current = pendingRetryCallback;
  }, [pendingRetryCallback]);

  // ============================================
  // TOKEN REFRESH LISTENER (App Mode Only)
  // ============================================
  useEffect(() => {
    // Listen for messages from React Native app (token refresh)
    if (!inApp || typeof window === 'undefined') return;

    const handleAppMessage = (event) => {
      try {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.type === 'token_refreshed' && data.tokenId) {
          if (isDevelopment()) {
            console.log('[SmartSend] Received refreshed token from app:', {
              hasToken: !!data.tokenId,
              hasPendingRetry: !!pendingRetryCallbackRef.current,
              orderId,
              isRefreshingNonce: isRefreshingNonceRef.current,
            });
          }

          // Prevent multiple simultaneous nonce refresh attempts
          if (isRefreshingNonceRef.current) {
            if (isDevelopment()) {
              console.warn(
                '[SmartSend] Nonce refresh already in progress, ignoring duplicate token refresh'
              );
            }
            return;
          }

          // Update token
          setToken(data.tokenId);

          // Auto-retry pending submission if callback exists
          if (typeof pendingRetryCallbackRef.current === 'function') {
            if (isDevelopment()) {
              console.log(
                '[SmartSend] Fetching new nonce before retrying submission'
              );
            }
            toast.success('Session refreshed. Updating security token...');

            // Mark nonce refresh as in progress
            isRefreshingNonceRef.current = true;

            // Capture callback to avoid race conditions
            const retryCallback = pendingRetryCallbackRef.current;

            // Fetch new nonce with refreshed token before retrying
            // This is critical: the old nonce is invalid after token refresh
            if (orderId) {
              getSmartSendOrderData(orderId, data.tokenId)
                .then((response) => {
                  // Check if callback still exists (component might have unmounted)
                  if (!pendingRetryCallbackRef.current) {
                    if (isDevelopment()) {
                      console.warn(
                        '[SmartSend] Callback cleared, aborting retry after nonce fetch'
                      );
                    }
                    isRefreshingNonceRef.current = false;
                    return;
                  }

                  if (response.secureVerifyHeader) {
                    if (isDevelopment()) {
                      console.log(
                        '[SmartSend] New nonce obtained, updating context and retrying'
                      );
                    }
                    // Update nonce in context
                    setSecureHeader(response.secureVerifyHeader);
                    // Execute retry after short delay to ensure state is updated
                    setTimeout(() => {
                      // Double-check callback still exists before retrying
                      if (pendingRetryCallbackRef.current) {
                        retryCallback();
                        setPendingRetryCallback(null);
                      }
                      isRefreshingNonceRef.current = false;
                    }, 300);
                  } else {
                    if (isDevelopment()) {
                      console.warn(
                        '[SmartSend] No nonce in response, retrying anyway'
                      );
                    }
                    // Retry even if no nonce (shouldn't happen, but handle gracefully)
                    setTimeout(() => {
                      if (pendingRetryCallbackRef.current) {
                        retryCallback();
                        setPendingRetryCallback(null);
                      }
                      isRefreshingNonceRef.current = false;
                    }, 300);
                  }
                })
                .catch((error) => {
                  console.error(
                    '[SmartSend] Failed to fetch new nonce after token refresh:',
                    error
                  );
                  toast.error(
                    'Failed to update security token. Please try again.'
                  );
                  setPendingRetryCallback(null);
                  isRefreshingNonceRef.current = false;
                });
            } else {
              // No orderId - retry without fetching new nonce (shouldn't happen)
              if (isDevelopment()) {
                console.warn(
                  '[SmartSend] No orderId available, retrying without new nonce'
                );
              }
              setTimeout(() => {
                if (pendingRetryCallbackRef.current) {
                  retryCallback();
                  setPendingRetryCallback(null);
                }
                isRefreshingNonceRef.current = false;
              }, 300);
            }
          } else {
            toast.success('Session refreshed successfully.');
          }
        } else if (data.type === 'token_refresh_failed') {
          if (isDevelopment()) {
            console.error('[SmartSend] Token refresh failed:', data.error);
          }

          // Clear pending callback since refresh failed
          setPendingRetryCallback(null);
          // Reset nonce refresh flag
          isRefreshingNonceRef.current = false;

          // Show error to user
          toast.error(
            'Session refresh failed. Please close and reopen the form.'
          );
        }
      } catch (error) {
        // Ignore non-JSON messages or parsing errors
        if (isDevelopment()) {
          console.warn('[SmartSend] Failed to parse app message:', error);
        }
      }
    };

    window.addEventListener('message', handleAppMessage);
    return () => {
      window.removeEventListener('message', handleAppMessage);
      // Reset nonce refresh flag on cleanup
      isRefreshingNonceRef.current = false;
    };
  }, [inApp, orderId]); // orderId needed for fetching new nonce after token refresh

  // ============================================
  // PHONE & VERIFICATION ACTIONS
  // ============================================
  const updatePhoneNumber = useCallback((phone) => {
    setPhoneNumber(phone);
  }, []);

  const confirmPhone = useCallback(() => {
    // Save phone number immediately with isPhoneVerified: false
    // This allows persistence across page refreshes
    if (orderId && phoneNumber) {
      savePhoneNumber(orderId, phoneNumber, false);

      // Track phone number entered when user confirms (clicks confirm button)
      // This ensures we only track once per confirmation, not on every keystroke
      // Note: phoneNumber is already truthy (checked in outer if condition)
      const formattedPhone = typeof phoneNumber === 'string' ? phoneNumber.trim() : '';
      if (formattedPhone) {
        analytics.track('Phone Number Entered', {
          source: ANALYTICS_SOURCE,
          phone: formattedPhone,
          orderId,
        });
      }
    }
    setVerificationStep('code');
  }, [orderId, phoneNumber]);

  const verifyCode = useCallback(
    (code, isSuccess, options = {}) => {
      if (isSuccess) {
        setVerificationCode(code); // Store code for final submission
        setIsCodeVerified(true);

        // Update phone number status to verified
        // Both phone and code are stored with 15-minute TTL to limit exposure window
        if (orderId && phoneNumber && typeof phoneNumber === 'string' && phoneNumber.trim()) {
          savePhoneNumber(orderId, phoneNumber, true);
          saveVerificationCode(orderId, code);

          // Track phone number verified
          analytics.track('SmartSend Phone Number Verified', {
            source: ANALYTICS_SOURCE,
            'phone.number': phoneNumber,
            orderId,
          });
        }

        // Check if personal message exists - show it before address selection
        const hasPersonalMessage = hasValidPersonalMessage(order);

        // Auto-advance logic: Desktop users skip "Redeem in App" screen
        if (options.autoAdvanceForDesktop && isDesktop) {
          if (isDevelopment()) {
            console.log(
              '[Desktop Auto-Advance] Verification complete',
              {
                isDesktop,
                phoneNumber,
                hasPersonalMessage,
              }
            );
          }
          // Show personal message if it exists, otherwise go to complete
          setVerificationStep(hasPersonalMessage ? 'personalMessage' : 'complete');
        } else if (options.advanceToComplete) {
          // Show personal message if it exists, otherwise go to complete
          setVerificationStep(hasPersonalMessage ? 'personalMessage' : 'complete');
        }
      } else {
        setIsCodeVerified(false);
        setVerificationCode('');
      }
    },
    [phoneNumber, orderId, isDesktop, order]
  );

  const skipToDeliveryForm = useCallback(() => {
    // Check if personal message exists - show it before address selection
    const hasPersonalMessage = hasValidPersonalMessage(order);

    // Show personal message if it exists, otherwise go to complete
    setVerificationStep(hasPersonalMessage ? 'personalMessage' : 'complete');
  }, [order]);

  /**
   * Advance from personal message step to address selection
   * Called when user clicks Continue after viewing personal message
   */
  const continueFromPersonalMessage = useCallback(() => {
    setVerificationStep('complete');
  }, []);

  /**
   * Clear phone number and return to phone input step
   * Used when user wants to use a different phone number
   * Clears cooldown so user can immediately request a code for the new number
   */
  const clearPhoneAndReset = useCallback(() => {
    if (orderId) {
      clearPhoneNumber(orderId);
      // Clear cooldown for new phone number - user shouldn't wait for a different number
      clearResendCooldown(orderId);
    }
    setPhoneNumber('');
    setVerificationStep('phone');
  }, [orderId]);

  // ============================================
  // ADDRESS ACTIONS
  // ============================================
  const updateAddress = useCallback(
    (addressData) => {
      setAddress(addressData);
      if (orderId && addressData) {
        saveAddress(orderId, addressData);
      } else if (orderId) {
        clearStoredAddress(orderId);
      }
    },
    [orderId]
  );

  const clearAddress = useCallback(() => {
    setAddress(null);
    if (orderId) {
      clearStoredAddress(orderId);
    } else {
      clearStoredAddress();
    }
  }, [orderId]);

  // ============================================
  // ZONING ACTIONS
  // ============================================
  const updateZoningData = useCallback(
    (data) => {
      setZoningData(data);
      if (orderId && data) {
        saveZoningData(orderId, data);
        setIsAddressValidated(true);
      } else if (orderId && !data) {
        clearStoredZoningData(orderId);
        setIsAddressValidated(false);
      } else {
        setIsAddressValidated(!!data);
      }
    },
    [orderId]
  );

  const clearZoning = useCallback(() => {
    setZoningData(null);
    if (orderId) {
      clearStoredZoningData(orderId);
    } else {
      clearStoredZoningData();
    }
    setIsAddressValidated(false);
  }, [orderId]);

  // ============================================
  // DELIVERY ACTIONS
  // ============================================
  const updateDeliveryDetails = useCallback(({ date, time, instructions }) => {
    if (date !== undefined) setDeliveryDate(date);
    if (time !== undefined) setDeliveryTime(time);
    if (instructions !== undefined) setDeliveryInstructions(instructions);

    // Note: Tracking for "Date/Time Selected" happens at the user action level
    // in DeliveryFormStep.jsx (handleDateSelect and onTimeSelect handlers)
    // This prevents duplicate tracking and ensures we only track user-initiated selections,
    // not programmatic updates (e.g., restoring from localStorage or setting defaults)
  }, []);

  const clearDeliveryDetails = useCallback(() => {
    setDeliveryDate('');
    setDeliveryTime('');
    setDeliveryInstructions('');
  }, []);

  const clearDeliveryDateAndTime = useCallback(() => {
    // Only clear date and time, preserve delivery instructions
    setDeliveryDate('');
    setDeliveryTime('');
  }, []);

  // ============================================
  // RECIPIENT INFO ACTIONS
  // ============================================
  const updateRecipientInfo = useCallback(
    (info) => {
      setRecipientInfo((prev) => {
        const updated = { ...prev, ...info };
        if (orderId) {
          saveRecipientInfo(orderId, updated);
        }
        return updated;
      });
    },
    [orderId]
  );

  // ============================================
  // FORM SUBMISSION
  // ============================================
  const submitDeliveryForm = useCallback(
    async (apiCall, options = {}) => {
      // Extract ssid from order data
      const ssid = order?.smartSendId;

      if (!ssid) {
        console.error('Missing smartSendId in order data');
        return {
          success: false,
          error:
            'Missing required order information. Please refresh and try again.',
        };
      }

      // Validate address data
      if (!address || !address.address1) {
        console.error('Missing or invalid address data:', address);
        return {
          success: false,
          error: 'Missing delivery address. Please select an address.',
        };
      }

      // Validate deliveryDate exists and is a valid date string (YYYY-MM-DD format)
      if (
        !deliveryDate ||
        typeof deliveryDate !== 'string' ||
        !deliveryDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        console.error('Missing or invalid delivery date:', deliveryDate);
        return {
          success: false,
          error: 'Please select a delivery date.',
        };
      }

      // Check if deliveryTime is required
      // Priority: order.displayDeliveryTime (authoritative for order type like Goody) >
      //           deliveryConfig.displayDeliveryTime > default true
      const displayDeliveryTime =
        order?.displayDeliveryTime ??
        zoningData?.deliveryConfig?.displayDeliveryTime ??
        true;

      // Validate deliveryTime when it's required
      if (displayDeliveryTime && !deliveryTime) {
        console.error('Missing delivery time (required):', {
          displayDeliveryTime,
          deliveryTime,
        });
        return {
          success: false,
          error: 'Please select a delivery time.',
        };
      }

      // Get timezone from address
      const timezone = address?.timezone || 'America/New_York';

      // Parse the selected date (now safe to parse since we've validated format)
      const selectedDate = parseDateInput(deliveryDate);

      // Calculate delivery_date timestamp (midnight in recipient timezone)
      const deliveryDateTimestamp = getDateTimestamp(selectedDate, timezone);

      // Format delivery_date_text
      const deliveryDateText = formatDateForDisplay(selectedDate, timezone);

      // Calculate delivery_time timestamp and text
      let deliveryTimeTimestamp;
      let deliveryTimeText;

      if (deliveryTime === 'within-2-hours') {
        deliveryTimeTimestamp = getCurrentTimePlusTwoHours(timezone);
        deliveryTimeText = 'Within 2 hours';
      } else if (deliveryTime === '10am-2pm') {
        deliveryTimeTimestamp = getTimeSlotTimestamp(
          selectedDate,
          '10am-2pm',
          timezone
        );
        deliveryTimeText = '10am–2pm';
      } else if (deliveryTime === '2pm-8pm') {
        deliveryTimeTimestamp = getTimeSlotTimestamp(
          selectedDate,
          '2pm-8pm',
          timezone
        );
        deliveryTimeText = '2pm–8pm';
      } else {
        // Fallback
        deliveryTimeTimestamp = deliveryDateTimestamp;
        deliveryTimeText = deliveryTime;
      }

      // Format full address for display
      const formattedAddress = [
        address?.address1,
        address?.address2,
        address?.locality,
        `${address?.state} ${address?.postcode}`,
        address?.country,
      ]
        .filter(Boolean)
        .join(', ');

      // Determine order type to conditionally include delivery instructions
      // Only gesture orders support delivery instructions
      const orderType =
        order?.order_type ||
        order?.orderType ||
        order?.type ||
        order?.skuType ||
        order?.productType ||
        'gesture';
      const normalizedOrderType = String(orderType).toLowerCase();
      const isGestureOrder = normalizedOrderType === 'gesture';

      // Build submission payload - using field names from legacy SmartSend
      const submissionData = {
        address_street: address?.address1 || '',
        address_extra: address?.address2 || '',
        address_city: address?.locality || '',
        address_state: address?.state || '',
        address_postalCode: address?.postcode || '',
        address_country: address?.country || '',
        address_formatted: formattedAddress,
        address_lat: address?.lat || 0,
        address_lng: address?.lng || 0,
        delivery_date: deliveryDateTimestamp,
        delivery_date_text: deliveryDateText,
        delivery_time: deliveryTimeTimestamp,
        delivery_time_text: deliveryTimeText,
        recipientDeliveryInstructions: isGestureOrder
          ? deliveryInstructions || ''
          : null,
        timezone,
        // Include zoning data fields
        isProductDeliverable: zoningData?.isProductDeliverable || true,
        offset: zoningData?.offset || 0,
        latestDeliveryOffset: zoningData?.latestDeliveryOffset || 7,
      };

      // Add merchantUid if available
      if (zoningData?.merchantUid) {
        submissionData.merchantUid = zoningData.merchantUid;
      }

      // Add tokenId to request body (required by backend)
      if (token) {
        submissionData.tokenId = token;
      }

      // Add URL query param code (from deep linking) - passed as 'code'
      if (urlCode) {
        submissionData.code = urlCode;
      }

      // Add SMS verification code (user-entered during phone verification) - passed as 'verificationCode'
      // Priority: options.verificationCode (for immediate retry after verification) > context verificationCode
      // Only use code if it's a valid non-empty string
      const codeToUse = options.verificationCode || verificationCode;
      if (
        codeToUse &&
        typeof codeToUse === 'string' &&
        codeToUse.trim()
      ) {
        submissionData.verificationCode = codeToUse;
      }

      const mergedRecipientInfo = {
        ...recipientInfo,
        ...(options.recipientInfo || {}),
      };

      // Always include recipient fields (even if empty) so backend can validate them
      // Legacy fields used by completeNina_Registration/completeNina_NonRegistration
      submissionData.recipientFirstName = mergedRecipientInfo.firstName ?? '';
      submissionData.recipientLastName = mergedRecipientInfo.lastName ?? '';
      submissionData.recipientEmail = mergedRecipientInfo.email ?? '';

      // Identity validation fields (used by checkSmartSendRecipientIdentity)
      // Backend expects recipient_name (combined), email, and phoneNumber
      const recipientName = [
        mergedRecipientInfo.firstName,
        mergedRecipientInfo.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      submissionData.recipient_name = recipientName;
      submissionData.email = mergedRecipientInfo.email ?? '';

      // Phone number handling: Backend only expects phoneNumber (not recipientPhone)
      // Use phone from missing info form if provided, otherwise use verified phoneNumber from context
      // Format to E164 (e.g., +14155552671) as required by backend
      const phoneToUse = mergedRecipientInfo.phone || phoneNumber || '';
      submissionData.phoneNumber = phoneToUse ? formatPhoneToE164(phoneToUse) : '';

      // Log full submission object for debugging (especially when missing info is being submitted)
      if (isDevelopment()) {
        const hasRecipientInfo = Boolean(
          mergedRecipientInfo.firstName ||
            mergedRecipientInfo.lastName ||
            mergedRecipientInfo.email ||
            mergedRecipientInfo.phone
        );
        console.log('🔍 [SmartSend Submission] Full submission payload:', {
          hasRecipientInfo,
          mergedRecipientInfo,
          recipientFields: {
            recipientFirstName: submissionData.recipientFirstName,
            recipientLastName: submissionData.recipientLastName,
            recipientEmail: submissionData.recipientEmail,
            recipient_name: submissionData.recipient_name,
            email: submissionData.email,
            phoneNumber: submissionData.phoneNumber,
          },
          fullSubmissionData: JSON.parse(JSON.stringify(submissionData)), // Deep clone to avoid reference issues
        });
      }

      // Call the API with ssid, submission data, nonce, and auth token
      const result = await apiCall(ssid, submissionData, secureHeader, token);

      // If submission failed and new nonce provided, update it for retry
      if (!result.success && result.newNonce) {
        setSecureHeader(result.newNonce);
      }

      return result;
    },
    [
      orderId,
      token,
      order,
      address,
      deliveryDate,
      deliveryTime,
      deliveryInstructions,
      secureHeader,
      zoningData,
      urlCode,
      verificationCode,
      recipientInfo,
      phoneNumber,
    ]
  );

  // ============================================
  // VALIDATION HELPERS
  // ============================================
  const isDeliveryFormValid = useCallback(() => {
    return Boolean(
      address && isAddressValidated && deliveryDate && deliveryTime
    );
  }, [address, isAddressValidated, deliveryDate, deliveryTime]);

  // ============================================
  // CONTEXT VALUE
  // ============================================
  const value = {
    // Order & Auth
    orderId,
    token,
    order,
    secureHeader,
    setSecureHeader,
    appMode,
    setToken,
    setOrder,
    setPendingRetryCallback, // For token refresh auto-retry

    // Verification Flow
    verificationStep,
    setVerificationStep,
    phoneNumber,
    updatePhoneNumber,
    confirmPhone,
    verificationCode,
    setVerificationCode,
    isLoadingDeepLink,
    setIsLoadingDeepLink,
    isVerifyingCode,
    setIsVerifyingCode,
    isCodeVerified,
    verifyCode,
    skipToDeliveryForm,
    continueFromPersonalMessage,
    clearPhoneAndReset,
    isDesktop, // Device detection for conditional flow

    // Address
    address,
    updateAddress,
    clearAddress,
    isAddressValidated,
    setIsAddressValidated,

    // Zoning
    zoningData,
    updateZoningData,
    clearZoning,

    // Delivery Details
    deliveryDate,
    deliveryTime,
    deliveryInstructions,
    setDeliveryDate,
    setDeliveryTime,
    setDeliveryInstructions,
    updateDeliveryDetails,
    clearDeliveryDetails,
    clearDeliveryDateAndTime, // Clear only date/time, preserve instructions

    // Recipient Info
    recipientInfo,
    updateRecipientInfo,

    // Form Submission
    submitDeliveryForm,
    isDeliveryFormValid,
  };

  return (
    <SmartSendContext.Provider value={value}>
      {children}
    </SmartSendContext.Provider>
  );
};

/**
 * Custom hook to use SmartSend context
 * @throws {Error} if used outside of SmartSendProvider
 */
export const useSmartSend = () => {
  const context = useContext(SmartSendContext);

  if (context === undefined) {
    throw new Error('useSmartSend must be used within a SmartSendProvider');
  }

  return context;
};

/**
 * Convenience hooks for specific context slices
 */

// Hook for verification flow state and actions
export const useVerification = () => {
  const {
    verificationStep,
    setVerificationStep,
    phoneNumber,
    updatePhoneNumber,
    confirmPhone,
    verificationCode,
    setVerificationCode,
    isLoadingDeepLink,
    setIsLoadingDeepLink,
    isVerifyingCode,
    setIsVerifyingCode,
    isCodeVerified,
    verifyCode,
    skipToDeliveryForm,
    continueFromPersonalMessage,
    clearPhoneAndReset,
    isDesktop,
  } = useSmartSend();

  return {
    verificationStep,
    setVerificationStep,
    phoneNumber,
    updatePhoneNumber,
    confirmPhone,
    verificationCode,
    setVerificationCode,
    isLoadingDeepLink,
    setIsLoadingDeepLink,
    isVerifyingCode,
    setIsVerifyingCode,
    isCodeVerified,
    verifyCode,
    skipToDeliveryForm,
    continueFromPersonalMessage,
    clearPhoneAndReset,
    isDesktop,
  };
};

// Hook for address state and actions
export const useAddress = () => {
  const {
    address,
    updateAddress,
    clearAddress,
    isAddressValidated,
    zoningData,
    updateZoningData,
    clearZoning,
  } = useSmartSend();

  return {
    address,
    updateAddress,
    clearAddress,
    isAddressValidated,
    zoningData,
    updateZoningData,
    clearZoning,
  };
};

// Hook for delivery details state and actions
export const useDelivery = () => {
  const {
    deliveryDate,
    deliveryTime,
    deliveryInstructions,
    setDeliveryDate,
    setDeliveryTime,
    setDeliveryInstructions,
    updateDeliveryDetails,
    clearDeliveryDetails,
    clearDeliveryDateAndTime,
    isDeliveryFormValid,
    submitDeliveryForm,
  } = useSmartSend();

  return {
    deliveryDate,
    deliveryTime,
    deliveryInstructions,
    setDeliveryDate,
    setDeliveryTime,
    setDeliveryInstructions,
    updateDeliveryDetails,
    clearDeliveryDetails,
    clearDeliveryDateAndTime,
    isDeliveryFormValid,
    submitDeliveryForm,
  };
};

export default SmartSendContext;
